<template>
  <DialogModal
    :title="$t('settingsDialogTitle', 'Change settings')"
    :width="900"
    hide-buttons
    icon="cog"
    @cancel="$emit('cancel')"
  >
    <div class="bk bk-settings">
      <div v-for="group in groups" :key="group.key" class="bk-form-section">
        <h3 class="bk-settings-group-title">
          <span>{{ group.label }}</span>
          <span v-if="group.id === 'beta'" class="bk-beta-indicator">BETA</span>
        </h3>
        <div>
          <FeatureSetting
            v-for="setting in group.settings"
            :key="group.key + setting.settingsKey"
            :feature-id="setting.featureId"
            :settings-key="setting.settingsKey"
            :setting="setting.setting"
          />
        </div>
      </div>
    </div>
  </DialogModal>
</template>

<script setup>
import { useBlokkli, computed } from "#imports";
import { DialogModal } from "#blokkli/components";
import FeatureSetting from "./FeatureSetting/index.vue";
import { SETTINGS_GROUP } from "#blokkli/constants";
import { settingsOverride } from "#blokkli-build/config";
const { $t, features, ui } = useBlokkli();
const getTranslation = $t;
const getGroupLabel = (key) => {
  if (key === "behavior") {
    return $t("settingsBehaviour", "Behaviour");
  } else if (key === "appearance") {
    return $t("settingsAppearance", "Appearance");
  } else if (key === "advanced") {
    return $t("settingsAdvanced", "Advanced");
  } else if (key === "artboard") {
    return $t("settingsArtboard", "Artboard");
  } else if (key === "beta") {
    return $t("settingsBeta", "New Features");
  }
  return key;
};
const getGroupIcon = (key) => {
  if (key === "behavior") {
    return "tools";
  } else if (key === "appearance") {
    return "palette";
  } else if (key === "advanced") {
    return "bug";
  }
  return "question";
};
const shouldRenderSetting = (key, setting) => {
  if (settingsOverride[key]?.disable) {
    return false;
  }
  if (setting.viewports?.length) {
    return setting.viewports.includes(ui.appViewport.value);
  }
  return true;
};
const settingTypeOrder = ["checkbox", "slider", "method"];
const groups = computed(() => {
  const settingGroups = features.mountedFeatures.value.reduce((acc, feature) => {
    Object.entries(feature.settings || {}).forEach(([settingsKey, setting]) => {
      const key = `feature:${feature.id}:${settingsKey}`;
      if (shouldRenderSetting(key, setting)) {
        const group = setting.group || "advanced";
        if (!acc[group]) {
          acc[group] = {
            id: group,
            key: group,
            label: getGroupLabel(group),
            icon: getGroupIcon(group),
            settings: []
          };
        }
        acc[group].settings.push({
          featureId: feature.id,
          settingsKey,
          setting
        });
      }
    });
    return acc;
  }, {});
  if (features.betaFeatures.value.length) {
    if (!settingGroups.beta) {
      settingGroups.beta = {
        id: "beta",
        key: "betaFeatures",
        label: getGroupLabel("beta"),
        icon: "bug",
        settings: []
      };
    }
    features.betaFeatures.value.forEach((v) => {
      const label = getTranslation(`feature_${v.id}_label`) || v.label;
      const description = getTranslation(`feature_${v.id}_description`) || v.description;
      settingGroups.beta.settings.push({
        featureId: "settings",
        settingsKey: "beta:" + v.id,
        setting: {
          type: "checkbox",
          default: false,
          label,
          description,
          group: "beta"
        }
      });
    });
  }
  return Object.values(settingGroups).map((group) => {
    group.settings.sort((a, b) => b.settingsKey.localeCompare(a.settingsKey)).sort(
      (a, b) => settingTypeOrder.indexOf(a.setting.type) - settingTypeOrder.indexOf(b.setting.type)
    );
    return group;
  }).sort((a, b) => SETTINGS_GROUP.indexOf(a.id) - SETTINGS_GROUP.indexOf(b.id));
});
defineEmits(["cancel"]);
</script>
